<template>
    <div v-if="widgetReady">
        <div class="row form-group">
            <div class="col-sm-12">
                <h4>Plan Details</h4>
            </div>
            <div class="col-sm-6 col-lg-4">
                <label>Plan Name</label><br />
                <input type="text" id="planName" class="form-control" placeholder="Plan name here" v-model="vm.planName">
            </div>
            <div class="col-sm--6 col-lg-4">
                <label>Plan Category</label><br />
                <select class="form-control" v-model="vm.planCategoryId" :disabled="!newPlan">
                    <option v-for="category in planCategories" :value="category.id">
                        {{ category.description }}
                    </option>
                </select>
            </div>
        </div>
        <hr/>
        <div class="row">
            <div class="col-sm-12">
                <h4>Price Tiers</h4>
                <p class="section-description">
                    Price tiers allow you to set age-banded rates, per age range and relationship, all under one plan.   
                    <br>
                    Note: Member must meet both age and relationship requirements to be offered this plan.
                </p>
                <div v-if="hasPatients" class="alert alert-warning">
                    Editing price tiers on plans in use will cause all changes made here to begin in the future
                    and will lock them from being modified until then.
                </div>
                <div class="row" style="margin:0">
                    <div class="col-sm-4 box"> 
                        <price-tiers ref="priceTiersPrimary" :plan-id="planId" :consumerRelationshipId="1"></price-tiers>
                    </div>
                    <div class="col-sm-4 box"> 
                        <price-tiers ref="priceTiersSpouse" :plan-id="planId" :consumerRelationshipId="2"></price-tiers>
                    </div>
                    <div class="col-sm-4 box"> 
                        <price-tiers ref="priceTiersDep" :plan-id="planId" :consumerRelationshipId="3"></price-tiers>
                    </div>
                </div>

            </div>
        </div>
        <hr/>
        <div>
            <h4>Recurring Membership Options</h4>
            <p class="section-description">
                Recurring Membership Options allow you to set the available membership options for a plan.
                You may allow for one or multiple membership options within the plan.
                All membership options are billed in advance and renewed automatically based on the term selected.
            </p>
            <div v-if="hasFrequencyTermination" class="alert alert-warning">
                Removing a plan period will cause all members, that are currently assigned to it,
                to not auto-renew at the end of their existing period.
            </div>
            <div class="row form-group">
                <div v-if="!vm.monthlyFrequencyOnly">
                    <div v-for="freq in planFrequencies.sort(x => x.monthFrequency)" :key="freq.planFrequencyId" class="col-md-2">
                        <input :id="'frequency-'+freq.planFrequencyId" type="checkbox" class="filled-in"
                            :value="freq.planFrequencyId" v-model="checkedFrequencies"/>
                        <label :for="'frequency-'+freq.planFrequencyId">
                            {{ freq.monthFrequency == 1 ? 'Monthly' : (freq.monthFrequency + '-month') }}
                        </label>
                    </div>
                </div>
            </div>
            <div class="form-group">
                <div class="switch">
                    Monthly Frequency Only?&nbsp;
                    <label>
                        No
                        <input :disabled="!newPlan" type="checkbox" v-model="vm.monthlyFrequencyOnly">
                        <span class="lever"></span> Yes
                    </label>
                </div>
            </div>
        </div>
        <hr/>
        <div style="text-align: left;">
            <h4>Discounts</h4>
            <p class="section-description">
                Family Discounts allow you to create a 'per person' discount percentage for family members.
                Discount is based on family members selecting the same plan and applies upon plan selection.
            </p>
            <button @click.prevent="addDiscount" class="btn btn btn-green btn-sm position-relative discount-button">
                <template v-if="discountLength > 0">
                    <span class="badge bg-danger discount-badge">{{ discountLength }} Pending Discount<tempate v-if="discountLength > 1">S</tempate></span>
                </template>
                <span v-if="hasDiscounts">
                    EDIT DISCOUNT
                </span>
                <span v-else>
                    ADD DISCOUNT
                </span>
            </button>
        </div>
        <hr />
        <div style="text-align: left;">
            <h4>Custom Email:</h4>
            <p class="section-description">
                Below are emails that you can customize for patients of this plan
            </p>
            <ul>
                <li v-for="email in customEmails" :key="email.emailTypeId">
                    <a @click.prevent="editTemplate(email)">{{email.emailTypeDesc}}</a>                
                    <span v-if="email.pending" class="badge bg-danger" style="background: #d03d5a;">
                        Pending Email
                    </span>
                </li>
            </ul>
        </div>
        <hr/>
        <div style="text-align: left;">
            <h4>Receipts:</h4>
            <div>
                <label>Custom Receipt Descriptor (optional)</label>
                <i data-toggle="tooltip" data-html="true" title="" data-placement="right" class="fas fa-info-circle wl-color" 
                    data-original-title="<div style='max-width:330px;position:relative;overflow:auto;'><div style='width:150px;float:left;text-align:left;padding-left:5px;'>This text will populate on the member out of pocket receipts to provide additional details.</div></div>">
                </i>
                <br />
                <textarea class="form-control" name="description" value="" v-model="vm.receiptDescription"
                    placeholder="Enter a custom receipt description here for your members to see on their receipts."></textarea>
            </div>
        </div>
        <hr/>
        <div v-if="newPlan">
            <div class="row">
                <div class="col-sm-12 col-md-6 col-lg-4">
                    <h4>Plan Type</h4>
                    <p>
                        <input type="checkbox" class="filled-in" id="tele"
                               v-model="vm.virtualPlan">
                        <label for="tele">Is this a virtual care plan?</label>
                    </p>
                </div>
            </div>
            <hr />
        </div>
        <div>
            <div class="row form-group">
                <div class="col-md-12 form-group">
                    <h4>PDF Upload</h4>
                    <div v-if="vm.fileId">
                        Existing File Name: {{vm.fileName}}
                        <button title="Remove file" class="btn-xs btn-red" 
                            style="line-height: normal;" @click="clearFile">
                            <i class="fa fa-trash" />
                        </button>
                    </div>
                    <div v-else>
                        <p>PDF files for plan presentment may be uploaded here</p>
                        <ac-file-selection ref="fileSelection" :valid-extensions="['pdf']">

                        </ac-file-selection>
                    </div>
                    <hr />
                </div>
                <div class="col-md-12">
                    <h4>Plan Details</h4>
                    <ac-rich-text-editor v-model="vm.openTemplate">

                    </ac-rich-text-editor>
                    <hr />
                </div>
            </div>
        </div>
        <!-- OBSOLETE: legacy plan settings (Office Visits / Wellness / Additional Services). Retained for reference; never shown unless showObsoleteLegacyPlanSettingsForm is true (dev comparison only). -->
        <div v-if="showObsoleteLegacyPlanSettingsForm" class="legacy-plan-settings-form--obsolete">
            <div class="row">
                <div class="col-xs-12 col-sm-12 col-md-12 col-lg-4">
                    <h4>Office Visits</h4>
                    <p>
                        <input type="checkbox" class="filled-in" id="u4"
                               v-model="vm.unlimitedOfficeVisits">
                        <label for="u4">Unlimited Primary Care Visits</label>
                    </p>
                    <div v-if="!vm.unlimitedOfficeVisits" class="form-group">
                        <label>Office visits</label>
                        <div class="input-group">
                            <div class="input-group-addon">#</div>
                            <input type="text" class="form-control form-short-field" v-model="vm.officeVisitsPerMonth" style="width: 30%;">
                        </div>
                    </div>
                    <p>
                        <label>Do you charge a Visit Fee?</label><br>
                        <input name="chargesVisitFee" type="radio" id="test1" v-model="vm.chargesVisitFee" :value="true">
                        <label for="test1">Yes</label>
                        <input name="chargesVisitFee" type="radio" id="test2"
                               v-model="vm.chargesVisitFee" :value="false">
                        <label for="test2">No</label>
                    </p>
                    <div class="form-group" v-if="vm.chargesVisitFee">
                        <label>What is your visit fee?</label>
                        <div class="input-group">
                            <div class="input-group-addon">$</div>
                            <input type="text" class="form-control form-short-field" placeholder="10.00"
                                   v-model="vm.visitFee" style="width: 30%;">
                        </div>
                    </div>
                    <p>
                        <label>Do you offer Same Day Availability?</label><br>
                        <input name="sameDayAvailability" type="radio" id="test11"
                               v-model="vm.sameDayAvailability" :value="true">
                        <label for="test11">Yes</label>
                        <input name="sameDayAvailability" type="radio" id="test12"
                               v-model="vm.sameDayAvailability" :value="false">
                        <label for="test12">No</label>
                    </p>
                    <p>
                        <label>Do you offer Acute Care Visits?</label><br>
                        <input name="acuteCareVisits" type="radio" id="test13" v-model="vm.acuteCareVisits" :value="true">
                        <label for="test13">Yes</label>
                        <input name="acuteCareVisits" type="radio" id="test14" v-model="vm.acuteCareVisits" :value="false">
                        <label for="test14">No</label>
                    </p>
                    <p>
                        <label>Are Acute Medications in Office included?</label><br>
                        <input name="medicationsIncluded" type="radio" id="test63" v-model="vm.medicationsIncluded" :value="true">
                        <label for="test63">Yes</label>
                        <input name="medicationsIncluded" type="radio" id="test64" v-model="vm.medicationsIncluded" :value="false">
                        <label for="test64">No</label>
                    </p>
                </div>

                <div class="col-xs-12 col-sm-12 col-md-12 col-lg-4">
                    <h4>After Hours (Urgent Care) Visits</h4>
                    <label><em>After hours access</em></label>
                    <p>
                        <select class="form-control form-med-field" v-model="vm.providerAfterHoursId">
                            <option v-for="pah in providerAfterHours" :value="pah.id">
                                {{ pah.name }}
                            </option>
                        </select>
                    </p>
                    <p>
                        <label>Do you charge an After Hours Urgent Care Fee?</label><br>
                        <input name="urgentCareFee" type="radio" id="test23"
                               v-model="vm.chargesUrgentCareFee" :value="true">
                        <label for="test23">Yes</label>
                        <input name="urgentCareFee" type="radio" id="test24"
                               v-model="vm.chargesUrgentCareFee" :value="false">
                        <label for="test24">No</label>
                    </p>
                    <div class="form-group" v-if="vm.chargesUrgentCareFee">
                        <label>What is your After hours fee?</label>
                        <div class="input-group">
                            <div class="input-group-addon">$</div>
                            <input type="text" class="form-control form-short-field" placeholder="10.00"
                                   v-model="vm.urgentCareFee" style="width: 30%;">
                        </div>
                    </div>
                    <p>
                        <label>Are Urgent Care In Office procedures included?</label><br>
                        <input name="urgentCareInOfficeIncluded" type="radio" id="testz3"
                               v-model="vm.urgentCareInOfficeIncluded" :value="true">
                        <label for="testz3">Yes</label>
                        <input name="urgentCareInOfficeIncluded" type="radio" id="testz4"
                               v-model="vm.urgentCareInOfficeIncluded" :value="false">
                        <label for="testz4">No</label>
                    </p>
                </div>
                <div class="col-xs-12 col-sm-12 col-md-12 col-lg-3">
                    <h4>Labs &amp; Imaging</h4>
                    <p>
                        <label>Discount on Labs</label><br>
                        <input name="discountLabs" type="radio" id="test22"
                               v-model="vm.discountLabs" :value="true">
                        <label for="test22">Yes</label>
                        <input name="discountLabs" type="radio" id="test123"
                               v-model="vm.discountLabs" :value="false">
                        <label for="test123">No</label>
                    </p>
                    <p>
                        <label>Basic Labs Included</label><br>
                        <input name="basicLabsIncluded" type="radio" id="test44"
                               v-model="vm.basicLabsIncluded" :value="true">
                        <label for="test44">Yes</label>
                        <input name="basicLabsIncluded" type="radio" id="test45"
                               v-model="vm.basicLabsIncluded" :value="false">
                        <label for="test45">No</label>
                    </p>
                    <p>
                        <label>Discount on Imaging</label><br>
                        <input name="discountImaging" type="radio" id="test40"
                               v-model="vm.discountImaging" :value="true">
                        <label for="test40">Yes</label>
                        <input name="discountImaging" type="radio" id="test41"
                               v-model="vm.discountImaging" :value="false">
                        <label for="test41">No</label>
                    </p>
                </div>
            </div>
            <div class="row">
                <div class="col-xs-12 col-sm-12 col-md-12 col-lg-8">
                    <h4>Wellness Programs</h4>
                    <div class="col-xs-12 col-sm-12 col-md-12 col-lg-6">
                        <label><em>Preventative Care</em></label>
                        <p>
                            <template v-for="pc in preventativeCare">
                                <input name="preventativeCare" type="radio"
                                       v-model="vm.preventativeCareId"
                                       :value="pc.id"
                                       :id="'pc'+pc.id">
                                <label :for="'pc'+pc.id">
                                    {{ pc.name }}
                                </label>
                                <br>
                            </template>
                        </p>
                        <p>
                            <input type="checkbox" class="filled-in" id="chronicDiseaseManagement"
                                   v-model="vm.chronicDiseaseManagement">
                            <label for="chronicDiseaseManagement">Chronic Disease Management</label>
                        </p>
                        <p>
                            <input type="checkbox" class="filled-in" id="healthRiskAssessment1"
                                   v-model="vm.healthRiskAssessment">
                            <label for="healthRiskAssessment1">Health Risk Assessment</label>
                        </p>
                        <p>
                            <input type="checkbox" class="filled-in" id="biometricScreenings"
                                   v-model="vm.biometricScreenings">
                            <label for="biometricScreenings">Biometric Screening</label>
                        </p>
                    </div>
                    <div class="col-xs-12 col-sm-12 col-md-12 col-lg-4">
                        <p>
                            <input type="checkbox" class="filled-in" id="weightLossManagement"
                                   v-model="vm.weightLossManagement">
                            <label for="weightLossManagement">Weight Loss Management</label>
                        </p>
                        <p>
                            <input type="checkbox" class="filled-in" id="stressManagement"
                                   v-model="vm.stressManagement">
                            <label for="stressManagement">Stress Management</label>
                        </p>

                        <p>
                            <input type="checkbox" class="filled-in" id="nutritionEvaluation"
                                   v-model="vm.nutritionEvaluation">
                            <label for="nutritionEvaluation">Nutrition Evaluation</label>
                        </p>
                        <p>
                            <input type="checkbox" class="filled-in" id="fitnessEvaluation"
                                   v-model="vm.fitnessEvaluation">
                            <label for="fitnessEvaluation">Fitness Evaluation</label>
                        </p>
                        <p>
                            <input type="checkbox" class="filled-in" id="sportsPhysical"
                                   v-model="vm.sportsPhysical">
                            <label for="sportsPhysical">Sports Physicals</label>
                        </p>
                        <p>
                            <input type="checkbox" class="filled-in" id="wellChildExam"
                                   v-model="vm.wellChildExam">
                            <label for="wellChildExam">Well-Child Exams</label>
                        </p>
                    </div>
                </div>
                <div class="col-xs-12 col-sm-12 col-md-12 col-lg-4">
                    <h4>Additional Services:</h4>
                    <p>
                        <input type="checkbox" class="filled-in" id="availableBusinessHours"
                               v-model="vm.availableBusinessHours">
                        <label for="availableBusinessHours">Physician Access during business hours</label>
                    </p>
                    <p>
                        <input type="checkbox" class="filled-in" id="availableAllHours"
                               v-model="vm.availableAllHours">
                        <label for="availableAllHours">24 hour access via Phone/Text/Email</label>
                    </p>
                    <p>
                        <input type="checkbox" class="filled-in" id="houseCalls" v-model="vm.houseCalls">
                        <label for="houseCalls">House Calls</label>
                    </p>
                    <div class="form-group">
                        <label>Please list any other services you offer.</label>
                        <textarea class="form-control form-long-field" rows="2"
                                  placeholder="Ex: Juice bar, Nutritionist on site, etc."
                                  v-model="vm.otherServices"></textarea>
                    </div>
                </div>
            </div>

        </div>
        <div class="row">
            <div class="col-md-12">
                <div class="pull-right">
                    <slot></slot>
                </div>
            </div>
        </div>
        <ac-auto-reset-modal ref="addDiscountModal"
                             :save-on-close="isExistingPlan"
                             :plan-id="planId"
                             :provider-entity-id="vm.providerEntityId"
                             :path="'components/shared/providers/plans/PlanDiscountModal.vue'">
        </ac-auto-reset-modal>
        <edit-email-templates ref="EditEmailTemplates"></edit-email-templates>
    </div>
</template>

<script>
    import {api} from "../../../../global/framework/api.js";
    import _ from 'lodash';
    import {configTables} from "../../../../global/configTables.js";
    import {PlanClass} from "./Plan.js";
    import PriceTiers from "./PriceTiers/PriceTiers.vue";
    import {notifications} from "../../../../global/notifications.js";
    import EditEmailTemplates from "../../../../../src/pages/root/components/system/EditEmailTemplates.vue"
    // import PlanDiscount from "./PlanDiscountModal.vue"

    export default {
        name: "Plan",
        components: {PriceTiers, EditEmailTemplates},
        // components: {PlanDiscount},
        props: {
            dataFunction: {type: [PlanClass], required: true},
            planId: {type: [Number], required: false, default: 0}
        },
        provide() {
            return {
                saveTemplate: this.saveTemplate,
            }
        },
        data() {
            return {
                widgetReady: false,
                planLevels: [],
                planCategories: [],
                providerAfterHours: [],
                preventativeCare: [],
                relationships: [],
                vm: {},
                /**
                 * Legacy structured plan settings UI — kept in template below but never mounted in production.
                 * Toggle true locally only if you need to compare or revive the old form.
                 */
                showObsoleteLegacyPlanSettingsForm: false,
                hasOpenTemplate: false,
                discounts: [],
                isExistingPlan: false,
                checkedFrequencies: [],
                customEmails: [],
                customEmail: null,
                hasPatients: false
            }
        },
        computed: {
            newPlan() {
                return !this.hasValue(this.planId);
            },
            hasDiscounts() {
                if (this.vm.discounts != null && this.vm.discounts.length > 0) {
                    return true;
                }
                if (this.discounts.length > 0) {
                    return true;
                }
                return false;
            },
            discountLength() {
                return this.discounts.length;
            },
            hasFrequencyTermination() {
                return this.vm.monthFrequencies && this.vm.monthFrequencies
                    .filter(y => this.checkedFrequencies && !this.checkedFrequencies.includes(y.planFrequencyId)).length > 0;
            },
        },
        async mounted() {
            let [pl, pcat, pc, pah, pf, rs] = await Promise.all([
                configTables.getPlanLevels(),
                configTables.getPlanCategories(),
                configTables.getPreventativeCareOptions(),
                configTables.getProviderAfterHoursOptions(),
                configTables.getPlanFrequencies(),
                configTables.getRelationships()
            ]);

            this.planLevels = pl;
            this.planCategories = pcat;
            this.preventativeCare = pc;
            this.providerAfterHours = pah;
            this.planFrequencies = pf;
            this.relationships = rs;

            if (this.hasValue(this.planId) && this.planId > 0) {
                let currentPlanData = await this.dataFunction.getPlan();
                this.mapData('vm', currentPlanData);
                // console.log("Plan mounted currentPlanData:", currentPlanData);
                this.hasOpenTemplate = this.hasValue(currentPlanData.openTemplate);
                this.customEmails = currentPlanData.customEmails;
                this.checkedFrequencies = currentPlanData.monthFrequencies.map(x => x.planFrequencyId);
                this.hasPatients = currentPlanData.hasPatients;
            } else {
                let template = await this.dataFunction.getPlanTemplate();
                this.customEmails = template.customEmails;
                template.preventativeCareId = this.preventativeCare[0].id;//default to first option
                template.providerAfterHoursId = this.providerAfterHours[0].id;//default to first option
                template.planCategoryId = 0;
                template.monthlyFrequencyOnly = template.monthlyFrequencyOnly || false;//default to false
                this.mapData('vm', template);
            }

            this.addWatchers();
            //activate tooltip
            $(function () {
                $('[data-toggle="tooltip"]').tooltip()
            });
            this.addEventListeners();
            if (this.planId > 0) {
                this.isExistingPlan = true
            }
            this.widgetReady = true;
        },
        methods: {
            addWatchers() {
                this.$watch('vm.monthlyFrequencyOnly', (newValue) => {
                    if (newValue) {
                        this.checkedFrequencies = [1];
                    }
                });
                this.$watch('vm.virtualPlan', (newValue) => {
                    this.dataFunction.getPlanTemplate().then((template) => {
                        template.virtualPlan = newValue;
                        this.reset(template);
                    });
                });
                // Legacy form field sync (only relevant if showObsoleteLegacyPlanSettingsForm is ever true)
                this.$watch('vm.unlimitedOfficeVisits', (newValue) => {
                    if (newValue) {
                        this.vm.officeVisitsPerMonth = null;
                    }
                });
                this.$watch('vm.chargesVisitFee', (newValue) => {
                    if (!newValue) {
                        this.vm.visitFee = null;
                    }
                });
                this.$watch('vm.chargesUrgentCareFee', (newValue) => {
                    if (!newValue) {
                        this.vm.urgentCareFee = null;
                    }
                });
            },
            getFileSelectionData() {
                if (!this.$refs.fileSelection) return null;
                return this.$refs.fileSelection.getFileData();
            },
            clearFile() {
                this.vm.fileId = null;
            },
            getData() {
                var primaryPT = this.$refs.priceTiersPrimary.getPriceTiers();
                var spousePT = this.$refs.priceTiersSpouse.getPriceTiers();
                var depPT = this.$refs.priceTiersDep.getPriceTiers();
                this.vm.priceTiers = primaryPT.concat(spousePT, depPT);
                this.vm.monthFrequencies = this.checkedFrequencies.map(x => {
                    return {planId: this.planId, planFrequencyId: x}
                });
                this.vm.discounts = this.discounts;
                this.vm.customEmails = this.customEmails.filter(x => x.pending);
                this.vm.planLevelId = 4;
                
                if (!this.validatePlanDetails()) return null;
                if (!this.validatePriceTiers()) return null;
                return this.vm;
            },
            validatePlanDetails() {
                let selection = this.getFileSelectionData();
                var missingFile = !this.vm.fileId && (!selection || !selection.file);
                let template = this.vm.openTemplate;
                var missingOpenTemplate = (!template || template.replaceAll("&nbsp;", "").trim().length < 1);

                if (missingFile && missingOpenTemplate) {
                    notifications.showError({
                        title: "Missing Custom Plan Details",
                        message: "Add plan details text or a PDF file upload."
                    });
                    return false;
                }
                return true;
            },
            validatePriceTiers() {
                if (this.$refs.priceTiersPrimary.anyIncompleteRows) {
                    this.$refs.priceTiersPrimary.showIncompleteAlert = true;
                    notifications.showError({title: "Incomplete Price Tiers", 
                        message: "There are unadded or incomplete primary price tiers"
                    });
                    return false;
                }
                if (this.$refs.priceTiersSpouse.anyIncompleteRows) {
                    this.$refs.priceTiersSpouse.showIncompleteAlert = true;
                    notifications.showError({title: "Incomplete Price Tiers", 
                        message: "There are unadded or incomplete spouse price tiers"
                    });
                    return false;
                }
                if (this.$refs.priceTiersDep.anyIncompleteRows) {
                    this.$refs.priceTiersDep.showIncompleteAlert = true;
                    notifications.showError({title: "Incomplete Price Tiers", 
                        message: "There are unadded or incomplete dependent price tiers"
                    });
                    return false;
                }
                return true;
            },
            addEventListeners() {
                this.$bus.$on(this.$bus.$AcEvents.DISCOUNT_ADDED, (eventData) => {
                    this.discounts = eventData;
                });
            },
            reset(template) {
                template.planName = this.vm.planName;
                template.adultFee = this.vm.adultFee;
                template.childFee = this.vm.childFee;
                template.minAge = this.vm.minAge;
                template.maxAge = this.vm.maxAge;
                template.preventativeCareId = this.preventativeCare[0].id;//default to first option
                template.providerAfterHoursId = this.providerAfterHours[0].id;//default to first option
                template.monthlyFrequencyOnly = this.vm.monthlyFrequencyOnly;
                template.planCategoryId = this.vm.planCategoryId;
                this.discounts = [];
                this.mapData('vm', template);
            },
            addDiscount() {
                let discountPlanInfo = {
                    discounts: [...this.discounts],
                    planId: this.planId
                }
                this.$refs.addDiscountModal.showModal(discountPlanInfo);
            },
            editTemplate(email) {
                var newb = _.clone(email);
                this.$refs.EditEmailTemplates.showModal(newb);
            },
            async saveTemplate(email) {                
                // console.log("vm: ", this.vm, "plan: ", this.planId);
                var index = this.customEmails.map(x => x.emailTypeId).indexOf(email.emailTypeId);
                if(this.vm.providerEntityId != null && this.vm.providerEntityId > 0){
                    let providerEntityId = this.vm.providerEntityId;                
                    if(this.planId != null && this.planId > 0) {
                        email.planId = this.planId;
                        let result = api.post('ProviderEntity', `${providerEntityId}/SaveEmail`, email);
                    }
                    else {
                        email.pending = true;
                    }
                }
                else if(this.vm.mediatorId != null && this.vm.mediatorId > 0){
                    let mediatorId = this.vm.mediatorId;
                    if(this.planId != null && this.planId > 0) {
                        email.planId = this.planId;
                        let result = api.post('MediatorPlan', `${mediatorId}/SaveEmail`, email);
                    }
                    else {
                        email.pending = true;
                    }
                }

                this.customEmails.splice(index, 1);                
                this.customEmails.push(email);                        
                    
                this.$refs.EditEmailTemplates.closeModal();
            },
        }
    }
</script>

<style scoped>
    [type="radio"]:not(:checked) + label, [type="radio"]:checked + label {
        padding-right: 35px;
    }

    .fa-info-circle {
        color: #2a9a71;
    }

    .section-description {
        margin-bottom: 15px;
    }

    .discount-button {
        overflow: visible;
    }

    .discount-badge {
        position: absolute;
        top: 0;
        right: -3px;
        transform: translateX(100%);
        background: #d03d5a;
        padding: 2px;
        border-radius: 4px;
        text-transform: capitalize;
    }

    .box {
        min-height: 200px;
        padding: 4px;
        width:32%;
        margin-left: 4px;
        margin-right: 4px;
    }
</style>